/**
 * useRouteCompletionStatus Hook
 *
 * Provides a Set of route IDs that the current user has sent (completed).
 * Used to show completion indicators on route pins and list items.
 *
 * @example
 * ```tsx
 * const { sentRouteIds, loading } = useRouteCompletionStatus(gymId);
 *
 * // Check if a route is sent
 * const isSent = sentRouteIds.has(route.id);
 * ```
 */

import { useMemo } from 'react';
import { useClimbLogs } from './useClimbLogs';

/**
 * Result from useRouteCompletionStatus hook
 */
export interface RouteCompletionStatusResult {
  /** Set of route IDs that have been sent by the user */
  sentRouteIds: Set<string>;
  /** Map of route IDs to their send log IDs (for unsending) */
  sendLogIds: Map<string, string>;
  /** Whether the data is still loading */
  loading: boolean;
  /** Any error message if query failed */
  error: string | null;
}

/**
 * Hook to get the set of route IDs that the current user has sent
 *
 * @param gymId - Optional gym ID to filter logs
 * @returns Object with sentRouteIds Set, loading, and error states
 */
export const useRouteCompletionStatus = (
  gymId: string | undefined
): RouteCompletionStatusResult => {
  const { logs, loading, error } = useClimbLogs({
    initialFilters: { gymId },
  });

  // Calculate set of sent route IDs and map of route ID to send log ID
  const { sentRouteIds, sendLogIds } = useMemo(() => {
    const sentIds = new Set<string>();
    const logIds = new Map<string, string>();

    for (const log of logs) {
      if (log.outcome === 'send') {
        sentIds.add(log.routeId);
        // Store the first send log ID for each route (for unsending)
        if (!logIds.has(log.routeId)) {
          logIds.set(log.routeId, log.id);
        }
      }
    }

    return { sentRouteIds: sentIds, sendLogIds: logIds };
  }, [logs]);

  return {
    sentRouteIds,
    sendLogIds,
    loading,
    error,
  };
};

export default useRouteCompletionStatus;
